import {Locale, useConfig} from '@aboutyou/config';
import React from 'react';

interface SchemaDataType {
  description: string;
  url: string;
  email: string;
}

const SchemaData: Record<number, Partial<Record<Locale, SchemaDataType>>> = {
  106117: {
    [Locale.en_AT]: {
      description:
        "ABOUT YOU is one of Europe's leading online fashion retailers, offering the latest trends from thousands of brands for women, men, and kids.",
      url: 'https://en.aboutyou.at/',
      email: 'customerservice@aboutyou.at',
    },
    [Locale.de_AT]: {
      description:
        'ABOUT YOU ist eines der führenden europäischen Mode- und Technologieunternehmen, das den Einkauf durch Personalisierung und mobile Nutzung revolutioniert. Mit einem umfangreichen Sortiment an Kleidung, Schuhen und Accessoires von Tausenden Marken für Damen, Herren und Kinder erreicht das Unternehmen Millionen von Kund:innen in ganz Europa.',
      url: 'https://www.aboutyou.at/',
      email: 'kundenservice@aboutyou.at',
    },
  },
};

export const AboutUsJsonLd = ({pageId}: {pageId: number}) => {
  const {locale} = useConfig();

  const pageData = SchemaData[pageId];
  if (!pageData) {
    return null;
  }

  const currentSchema = pageData[locale];

  if (!currentSchema) return null;

  const makeUrl = (path: string) => `${currentSchema.url}${path}`;

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          '@id': makeUrl('about-aboutyou#about-us'),
          url: makeUrl('about-aboutyou'),
          mainEntity: {
            '@type': 'OnlineStore',
            '@id': makeUrl('about-aboutyou#organization'),
            url: currentSchema.url,
            name: 'ABOUT YOU',
            alternateName: 'ABOUT YOU AT',
            description: currentSchema.description,
            legalName: 'ABOUT YOU SE & Co. KG',
            vatID: 'ATU76747228',
            foundingDate: '2014-05-05',
            email: currentSchema.email,
            parentOrganization: {
              '@type': 'Corporation',
              name: 'Zalando SE',
              '@id': 'https://corporate.zalando.com/de',
              url: 'https://corporate.zalando.com/de',
              sameAs: [
                'https://de.wikipedia.org/wiki/Zalando',
                'https://www.wikidata.org/wiki/Q136570',
                'https://www.google.com/search?kgmid=/m/0h66dk8',
              ],
            },
            sameAs: [
              'https://www.aboutyou.at/about-aboutyou',
              'https://www.aboutyou.at/',
              'https://corporate.aboutyou.de/de',
              'https://www.facebook.com/aboutyouglobal/',
              'https://www.instagram.com/aboutyoude',
              'https://x.com/aboutyou_com',
              'https://www.youtube.com/@aboutyou_com',
              'https://www.pinterest.com/aboutyoude',
              'https://www.tiktok.com/@aboutyou',
              'https://de.wikipedia.org/wiki/About_You',
              'https://www.wikidata.org/wiki/Q51844714',
              'https://www.crunchbase.com/organization/about-you',
              'https://www.northdata.de/ABOUT%20YOU%20Holding%20SE,%20Hamburg/HRB%20170972',
              'https://www.startbase.de/organization/aboutyou',
              'https://wellfound.com/company/about-you',
              'https://www.glassdoor.de/%C3%9Cberblick/Arbeit-bei-ABOUT-YOU-EI_IE1111018.11,20.htm',
              'https://play.google.com/store/apps/details?id=de.aboutyou.mobile.app',
              'https://apps.apple.com/de/app/about-you-mode-online-shop/id985244644',
              'https://www.google.com/search?kgmid=/g/11fv609r_n',
            ],
            logo: {
              '@type': 'ImageObject',
              contentUrl:
                'https://a.storyblok.com/f/290647847942542/4476/4c78a30332/aboutyou_logo.svg',
            },
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Domstraße 10',
              addressLocality: 'Hamburg',
              addressRegion: 'Hansestadt Hamburg',
              postalCode: '20095',
              addressCountry: 'DE',
            },
            subjectOf: [
              {
                '@type': 'Article',
                url: 'https://www.handelsblatt.com/finanzen/maerkte/aktien/ipo-erfolgreiches-boersendebuet-des-zalando-konkurrenten-about-you-aktie-legt-zu/27288300.html',
              },
              {
                '@type': 'Article',
                url: 'https://www.businessinsider.de/gruenderszene/business/about-you-ipo-details/',
              },
              {
                '@type': 'Article',
                url: 'https://www.manager-magazin.de/finanzen/boerse/about-you-aktie-startet-mit-dicken-aufschlaegen-an-der-boerse-a-e6d2544a-c0b1-475f-bf2a-e0d5605e8527',
              },
              {
                '@type': 'Article',
                url: 'https://www.wiwo.de/finanzen/boerse/online-modehaendler-das-sind-die-details-zum-about-you-boersengang/27264864.html',
              },
              {
                '@type': 'Article',
                url: 'https://www.spiegel.de/wirtschaft/unternehmen/about-you-bereitet-offenbar-boersengang-vor-a-9ceebac4-2d19-46fc-a571-80ad35926b65',
              },
            ],
            founder: [
              {
                '@type': 'Person',
                name: 'Tarek Müller',
                givenName: 'Tarek',
                familyName: 'Müller',
                jobTitle: 'CEO',
                worksFor: {
                  '@id': makeUrl('about-aboutyou#organization'),
                },
                sameAs: [
                  'https://de.wikipedia.org/wiki/Tarek_M%C3%BCller',
                  'https://www.forbes.com/profile/tarek-muller/',
                  'https://www.linkedin.com/in/tarek-m%C3%BCller',
                  'https://www.xing.com/profile/Tarek_Mueller',
                  'https://www.northdata.de/M%C3%BCller,%20Tarek,%20Hamburg/wst',
                  'https://x.com/tarekmueller',
                  'https://www.reddit.com/user/TarekMueller/',
                  'https://omr.com/de/events/festival/speakers/tarek-mueller-79598280-7bfb-48e4-b926-50fd2c19a074',
                  'https://www.startup-insider.com/person/tarek-mueller',
                  'https://www.google.com/search?kgmid=/g/11gdkn9rpq',
                  'https://www.wikidata.org/wiki/Q65503055',
                  'https://www.crunchbase.com/person/tarek-m%C3%BCller',
                ],
                subjectOf: [
                  {
                    '@type': 'Article',
                    url: 'https://omr.com/de/events/festival/speakers/tarek-mueller-79598280-7bfb-48e4-b926-50fd2c19a074',
                  },
                  {
                    '@type': 'Article',
                    url: 'https://www.acquisa.de/magazin/interview-tarek-mueller',
                  },
                ],
                image: {
                  '@type': 'ImageObject',
                  contentUrl:
                    'https://a.storyblok.com/f/290647847942542/566578/6701f6a24e/mueller_tarek_co-founder_co-ceo_marketing_brand_press.jpg',
                },
              },
              {
                '@type': 'Person',
                name: 'Sebastian Betz',
                givenName: 'Sebastian',
                familyName: 'Betz',
                jobTitle: 'CEO',
                worksFor: {
                  '@id': makeUrl('about-aboutyou#organization'),
                },
                sameAs: [
                  'https://www.linkedin.com/in/sebastian-betz-47280b18',
                  'https://www.forbes.com/profile/sebastian-betz/',
                  'https://www.google.com/search?kgmid=/g/11h12n0z1f',
                  'https://www.businessinsider.de/themen/sebastian-betz/',
                  'https://www.startup-insider.com/person/sebastian-betz',
                  'https://www.northdata.de/Betz,%20Sebastian,%20Hamburg/1iq',
                  'https://www.crunchbase.com/person/sebastian-betz',
                ],
                image: {
                  '@type': 'ImageObject',
                  contentUrl:
                    'https://a.storyblok.com/f/290647847942542/568740/8cb61c0424/betz_sebastian_co-founder_co-ceo_tech_product_press.jpg',
                },
              },
              {
                '@type': 'Person',
                name: 'Hannes Wiese',
                givenName: 'Hannes',
                familyName: 'Wiese',
                jobTitle: 'CEO',
                worksFor: {
                  '@id': makeUrl('about-aboutyou#organization'),
                },
                sameAs: [
                  'https://www.linkedin.com/in/hannes-wiese-4a915b165',
                  'https://www.startup-insider.com/person/hannes-wiese',
                  'https://www.northdata.de/Wiese,%20Hannes,%20Hamburg/c95',
                  'https://www.google.com/search?kgmid=/g/11h6tt2nsm',
                  'https://www.crunchbase.com/person/hannes-wiese-823e',
                ],
                image: {
                  '@type': 'ImageObject',
                  contentUrl:
                    'https://a.storyblok.com/f/290647847942542/358827/c7b076596d/wiese_hannes_co-founder_co-ceo_finance_operations_press.jpg',
                },
              },
            ],
          },
        }),
      }}
    ></script>
  );
};
